import i18next from "i18next";
import { initReactI18next } from "react-i18next";

import type { Language } from "./locale.js";
import en from "./locales/en.json";
import sv from "./locales/sv.json";

/**
 * Two bundles, Swedish and English, both loaded eagerly.
 *
 * Swedish is the interface's own language and the one it opens in; English was
 * asked for by the owner on 2026-10-07, and the decision is in
 * `project-scope.md`. Both are in the bundle rather than fetched when chosen,
 * because the second is a few dozen kilobytes and the application has to work
 * in a cellar with no signal — a language that arrives over the network is one
 * that can fail to arrive in exactly the place it is wanted.
 *
 * The rule from `tech-stack.md` still holds in its other half: no hard-coded
 * string. It is what made a second language a file rather than a rewrite.
 */

/**
 * Where the choice is remembered.
 *
 * In the browser, not on the account: it is a reading preference for this
 * screen rather than a fact about the person, it has to work on the sign-in
 * page before there is an account to ask, and it needs no migration and no
 * response field. The cost is honest — a phone and a laptop are chosen for
 * separately.
 */
const MEMORY_KEY = "corklog.language";

function remembered(): Language {
  try {
    const stored = localStorage.getItem(MEMORY_KEY);
    return stored === "en" ? "en" : "sv";
  } catch {
    // A private window or blocked storage: the interface's own language.
    return "sv";
  }
}

await i18next.use(initReactI18next).init({
  resources: { sv: { translation: sv }, en: { translation: en } },
  lng: remembered(),
  // A key missing in English shows the Swedish rather than the key, and the
  // test that compares the two bundles is what keeps that from happening.
  fallbackLng: "sv",
  interpolation: { escapeValue: false },
  returnNull: false,
});

document.documentElement.lang = i18next.language;

/** Switches the interface and remembers the choice for the next visit. */
export async function setLanguage(language: Language): Promise<void> {
  try {
    localStorage.setItem(MEMORY_KEY, language);
  } catch {
    // Not remembered, but still switched for as long as the page is open.
  }
  document.documentElement.lang = language;
  await i18next.changeLanguage(language);
}

export { i18next };
